import { useState, useEffect } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";
import { Package, Plus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import { formatCurrency, formatNumber } from "../utils/formatters";
import ServizioDialog from "./ServizioDialog";
import { AMBITI_CONTRATTO } from "../utils/ambitiContratto";
import {
  SectionCard, EmptyState, InfoTooltip, ICON_TILES,
  PRIMARY_BTN, SECONDARY_BTN, DIALOG_CONTENT_CLS,
} from "./designSystem";

// Riepilogo economico della lista compatta (design_handoff_catalogo_servizi,
// variante 2b): stessa grammatica di formato in pill e tabella, così la riga
// chiusa e il dettaglio non raccontano lo stesso prezzo in due modi diversi.
function valoreAddon(addon) {
  return addon.tipo_valore === "percentuale"
    ? `${formatNumber(addon.valore)}%`
    : formatCurrency(addon.valore, { decimals: 0 });
}

function pillAddon(addon) {
  return addon.ricorrenza === "mensile" ? `${valoreAddon(addon)}/mese` : `${valoreAddon(addon)} una tantum`;
}

// Prime lettere delle prime due parole: "TikTok Shop" → "TS".
function iniziali(nome) {
  return nome.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

const PILL_PREZZO = "inline-flex items-center h-7 px-2.5 rounded-[8px] text-[13px] font-semibold scheda-font-mono whitespace-nowrap";
const ETICHETTA_DETTAGLIO = "text-[10.5px] font-semibold uppercase tracking-[.09em] text-[#9095a1]";

function RigaVoce({ label, valore, unita, attivazione }) {
  return (
    <div className={`flex items-baseline gap-2.5 px-3.5 py-2.5 ${attivazione ? "bg-[#fbfbfd]" : "border-t border-[#eeeef5]"}`}>
      <span className={`flex-1 min-w-0 text-[13px] ${attivazione ? "font-semibold text-[#15161c]" : "font-medium text-[#565d6d]"}`}>{label}</span>
      <span className="text-[13.5px] font-semibold text-[#15161c] scheda-font-mono">{valore}</span>
      <span className={`w-[70px] text-right text-[11.5px] font-medium ${unita === "una tantum" ? "text-[#b07314]" : "text-[#9095a1]"}`}>{unita}</span>
    </div>
  );
}

// Pannello di configurazione del catalogo servizi offerti, usato dal wizard
// contratti per proporre la scelta del servizio venduto al cliente.
// Il montaggio è già condizionato dal chiamante (SettingsPage) al ruolo
// admin/direttore commerciale: qui non si ripete quel controllo.
// Creazione e modifica stanno in ServizioDialog. `ambito` "eventi" è il
// catalogo da cui sceglie la pagina Contratti eventi (backend/ambiti.py).
export default function CatalogoServiziPanel({ ambito = "standard" }) {
  const eventi = ambito === "eventi";
  const [servizi, setServizi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogAperto, setDialogAperto] = useState(false);
  const [inModifica, setInModifica] = useState(null);
  const [daEliminare, setDaEliminare] = useState(null);
  // Accordion: un solo servizio aperto alla volta. Si parte tutto chiuso;
  // dopo un salvataggio resta aperto quello appena creato o modificato.
  const [apertoId, setApertoId] = useState(null);

  useEffect(() => {
    caricaServizi();
  }, []);

  const caricaServizi = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/catalogo-servizi?ambito=${ambito}`);
      if (res.ok) {
        const data = await res.json();
        setServizi(data.items || []);
      } else {
        toast.error("Errore nel caricamento del catalogo servizi");
      }
    } catch {
      toast.error("Errore nel caricamento del catalogo servizi");
    } finally {
      setLoading(false);
    }
  };

  const apriNuovo = () => {
    setInModifica(null);
    setDialogAperto(true);
  };

  const apriModifica = (servizio) => {
    setInModifica(servizio);
    setDialogAperto(true);
  };

  const dopoSalvataggio = (salvato) => {
    setDialogAperto(false);
    setApertoId(salvato.servizio_id || null);
    caricaServizi();
  };

  const elimina = async () => {
    if (!daEliminare) return;
    try {
      const res = await apiFetch(`/catalogo-servizi/${daEliminare.servizio_id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Servizio eliminato");
        setServizi((prev) => prev.filter((s) => s.servizio_id !== daEliminare.servizio_id));
      } else {
        toast.error("Errore nell'eliminazione del servizio");
      }
    } catch {
      toast.error("Errore nell'eliminazione del servizio");
    } finally {
      setDaEliminare(null);
    }
  };

  return (
    <SectionCard
      icon={Package}
      iconBg={ICON_TILES.teal.bg}
      iconColor={ICON_TILES.teal.color}
      title={AMBITI_CONTRATTO[ambito].catalogo}
      count={loading ? null : servizi.length}
      testId={eventi ? "catalogo-servizi-eventi-section" : "catalogo-servizi-section"}
      action={
        <button type="button" onClick={apriNuovo} className={PRIMARY_BTN} data-testid={eventi ? "nuovo-servizio-eventi-btn" : "nuovo-servizio-btn"}>
          <Plus className="w-4 h-4" strokeWidth={2} />
          Nuovo servizio
        </button>
      }
    >
      <div className="flex flex-col gap-3.5">
        <p className="text-[13px] leading-[1.5] text-[#6b7280] -mt-1">
          {eventi
            ? "Servizi proponibili solo nei Contratti eventi: dati economici e testi della Proposta di Adesione."
            : "Servizi proponibili in fase di creazione contratto: dati economici e testi della Proposta di Adesione."}
        </p>
        {loading ? (
          <div className="h-24 rounded-[14px] bg-[#f3f3f8] animate-pulse" />
        ) : servizi.length === 0 ? (
          <EmptyState boxed>Nessun servizio configurato. Crea il primo per renderlo disponibile nella creazione contratti.</EmptyState>
        ) : (
          <div className="flex flex-col gap-2">
            {servizi.map((s) => {
              const aperto = apertoId === s.servizio_id;
              const addons = s.addons || [];
              return (
                <div
                  key={s.servizio_id}
                  className={`scroll-my-6 rounded-[14px] border bg-white overflow-hidden transition-[border-color,box-shadow] duration-300 ease-[cubic-bezier(.22,.7,.3,1)] ${aperto ? "border-[#d7d7f2] shadow-[0_12px_32px_-14px_rgba(40,40,110,.28),0_2px_6px_rgba(15,23,42,0.05)]" : "border-[#e9e9f1] shadow-[0_4px_16px_rgba(15,23,42,0.06),0_1px_3px_rgba(15,23,42,0.04)] hover:border-[#dcdce8] hover:shadow-[0_8px_24px_rgba(15,23,42,0.09)]"}`}
                  data-testid={`servizio-row-${s.servizio_id}`}
                >
                  <button
                    type="button"
                    onClick={() => setApertoId(aperto ? null : s.servizio_id)}
                    aria-expanded={aperto}
                    aria-controls={`servizio-dettaglio-${s.servizio_id}`}
                    className="w-full flex flex-wrap min-[820px]:flex-nowrap items-center gap-x-4 gap-y-3 px-[18px] py-4 text-left"
                    data-testid={`servizio-toggle-${s.servizio_id}`}
                  >
                    <span className="w-[38px] h-[38px] rounded-[11px] bg-[#eeeefc] text-[#5b5bd6] text-[13px] font-bold flex items-center justify-center shrink-0">
                      {iniziali(s.nome)}
                    </span>
                    <span className="flex-1 min-w-0 flex flex-col gap-1">
                      <span className="text-[15px] font-bold leading-[1.2] text-[#15161c]">{s.nome}</span>
                      {s.descrizione && (
                        <span className="text-[13px] font-medium leading-[1.3] text-[#6b7280] truncate">{s.descrizione}</span>
                      )}
                    </span>
                    {/* Sotto 820px le pill scendono sotto il testo, allineate al nome. */}
                    <span className="order-last w-full pl-[54px] justify-start min-[820px]:order-none min-[820px]:w-auto min-[820px]:pl-0 min-[820px]:justify-end flex flex-wrap items-center gap-1.5 shrink-0">
                      <span className={`${PILL_PREZZO} bg-[#15161c] text-white`}>{formatCurrency(s.importo_attivazione, { decimals: 0 })}</span>
                      {addons.map((a) => (
                        <span key={a.addon_id} className={`${PILL_PREZZO} bg-[#f3f3f8] text-[#3c414d]`}>{pillAddon(a)}</span>
                      ))}
                    </span>
                    <span className="w-7 h-7 flex items-center justify-center text-[#9095a1] shrink-0">
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-[250ms] ease-[cubic-bezier(.22,.7,.3,1)] ${aperto ? "rotate-180" : ""}`}
                        strokeWidth={2}
                      />
                    </span>
                  </button>

                  {/* Il dettaglio resta montato per poter animare anche la chiusura:
                      grid-template-rows 0fr→1fr fa scorrere l'altezza reale del
                      contenuto senza misurarla. `inert` lo toglie da tab e lettori
                      di schermo quando è chiuso. */}
                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(.22,.7,.3,1)] motion-reduce:transition-none ${aperto ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                    inert={!aperto}
                    // A fine apertura la pagina scorre quanto basta a mostrare
                    // il servizio intero: aperto in fondo alla lista, il
                    // dettaglio finirebbe sotto il bordo della finestra.
                    onTransitionEnd={(e) => {
                      if (aperto && e.target === e.currentTarget && e.propertyName === "grid-template-rows") {
                        e.currentTarget.parentElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
                      }
                    }}
                  >
                    <div className="min-h-0 overflow-hidden">
                    <div
                      id={`servizio-dettaglio-${s.servizio_id}`}
                      className={`grid grid-cols-1 min-[820px]:grid-cols-2 gap-6 px-[18px] pt-1 pb-[18px] min-[820px]:pl-[72px] transition-[opacity,transform] duration-300 ease-[cubic-bezier(.22,.7,.3,1)] motion-reduce:transition-none ${aperto ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1.5"}`}
                      data-testid={`servizio-dettaglio-${s.servizio_id}`}
                    >
                      <div className="flex flex-col gap-2 min-w-0">
                        <span className={ETICHETTA_DETTAGLIO}>Testo proposta di adesione</span>
                        <p className="text-[14px] leading-[1.6] text-[#3c414d] [text-wrap:pretty]">
                          {s.descrizione || <span className="text-[#9095a1]">Nessuna descrizione</span>}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 min-w-0">
                        <span className={ETICHETTA_DETTAGLIO}>Voci economiche</span>
                        <div className="flex flex-col border border-[#eeeef5] rounded-[11px] overflow-hidden">
                          <RigaVoce attivazione label={s.nome_attivazione || "Attivazione"} valore={formatCurrency(s.importo_attivazione, { decimals: 0 })} unita="una tantum" />
                          {addons.map((a) => (
                            <RigaVoce
                              key={a.addon_id}
                              label={a.label}
                              valore={valoreAddon(a)}
                              unita={a.ricorrenza === "mensile" ? "al mese" : "una tantum"}
                            />
                          ))}
                        </div>
                        <div className="flex justify-end gap-2 mt-1.5">
                          <InfoTooltip
                            title={`Elimina «${s.nome}»`}
                            description="Lo toglie dal catalogo: non sarà più proponibile nei nuovi contratti. Quelli già creati non cambiano."
                          >
                            <button
                              type="button"
                              onClick={() => setDaEliminare(s)}
                              className="h-8 px-3 rounded-[10px] text-[12.5px] font-semibold text-[#c0392b] hover:bg-[#fdecea] transition-colors"
                              data-testid={`elimina-servizio-${s.servizio_id}`}
                            >
                              Elimina
                            </button>
                          </InfoTooltip>
                          <button
                            type="button"
                            onClick={() => apriModifica(s)}
                            className={SECONDARY_BTN}
                            data-testid={`modifica-servizio-${s.servizio_id}`}
                          >
                            Modifica servizio
                          </button>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ServizioDialog
        open={dialogAperto}
        servizio={inModifica}
        ambito={ambito}
        onClose={() => setDialogAperto(false)}
        onSaved={dopoSalvataggio}
      />

      <AlertDialog open={!!daEliminare} onOpenChange={(open) => !open && setDaEliminare(null)}>
        <AlertDialogContent className={DIALOG_CONTENT_CLS} data-testid="elimina-servizio-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#15161c]">Eliminare "{daEliminare?.nome}"?</AlertDialogTitle>
            <AlertDialogDescription>
              L'operazione non è reversibile. Il servizio non sarà più proponibile nei nuovi contratti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            {/* Rosso pieno solo qui, come conferma dentro l'AlertDialog (docs/11-design-system.md). */}
            <AlertDialogAction
              onClick={elimina}
              className="h-9 px-5 rounded-[10px] bg-[#c0392b] hover:bg-[#a93226] text-white text-[13px] font-semibold transition-colors scheda-font-ui"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}
